import { useState, useEffect, useRef, useCallback } from 'react';
import {
  appLifecycleApi,
  type AppLifecycleStatus,
  type AppServiceInfo,
} from '../services/appLifecycleApi';
import { usePolling } from './usePolling';

export type AppStatus =
  | 'stopped'
  | 'starting'
  | 'running'
  | 'stopping'
  | 'partial'
  | 'error'
  | 'no_compose'
  | 'unknown';

export interface AppLifecycleState {
  status: AppStatus;
  services: AppServiceInfo[];
  hasCompose: boolean;
  appUrl: string | null;
  error: string | null;
}

const POLL_INTERVAL_TRANSITIONING = 20_000; // 20s while starting/stopping (aligned with backend cache TTL of 15s)
const POLL_INTERVAL_IDLE = 60_000; // 60s when running/stopped

const INITIAL_STATE: AppLifecycleState = {
  status: 'unknown',
  services: [],
  hasCompose: false,
  appUrl: null,
  error: null,
};

/** Faster cadence only while the app is mid-transition; idle otherwise. */
const intervalFor = (status: AppStatus | null): number =>
  status === 'starting' || status === 'stopping'
    ? POLL_INTERVAL_TRANSITIONING
    : POLL_INTERVAL_IDLE;

export function useAppLifecycle(projectId: string | undefined) {
  const [state, setState] = useState<AppLifecycleState>(INITIAL_STATE);
  const [actionError, setActionError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!projectId) setState(INITIAL_STATE);
  }, [projectId]);

  const isTransitioning = state.status === 'starting' || state.status === 'stopping';

  /**
   * Returns the status it just read. `usePolling` feeds that straight into
   * `intervalFor`, so the cadence always reflects the reading that produced it
   * — no ref, and no window where React state is a tick behind.
   */
  const fetchStatus = useCallback(async (): Promise<AppStatus | null> => {
    if (!projectId) return null;
    try {
      const data: AppLifecycleStatus = await appLifecycleApi.getStatus(projectId);
      if (!mountedRef.current) return null;

      const nextStatus = data.status as AppStatus;
      setState({
        status: nextStatus,
        services: data.services,
        hasCompose: data.has_compose,
        appUrl: data.app_url,
        error: data.error ?? null,
      });
      setActionError(null);
      return nextStatus;
    } catch {
      if (!mountedRef.current) return null;
      setState((prev) => ({
        ...prev,
        status: 'no_compose',
        hasCompose: false,
      }));
      return 'no_compose';
    }
  }, [projectId]);

  const { refreshNow } = usePolling<AppStatus>({
    fetch: fetchStatus,
    interval: intervalFor,
    enabled: !!projectId,
  });

  const beginTransition = useCallback(
    (next: 'starting' | 'stopping') => {
      setState((prev) => ({ ...prev, status: next }));
      // Re-fetch and re-pace immediately. Without this the user could wait up
      // to POLL_INTERVAL_IDLE (60s) for the first poll after clicking, because
      // the timer already in flight was scheduled at the idle cadence.
      refreshNow();
    },
    [refreshNow]
  );

  const startApp = useCallback(async () => {
    if (!projectId) return;
    setActionError(null);
    try {
      await appLifecycleApi.startApp(projectId);
      beginTransition('starting');
      // A second read a beat later, so the UI leaves the cached pre-start
      // snapshot quickly (the backend invalidated its cache at the same
      // moment, so this reflects the new lock). It can race the poll's next
      // tick; the backend cache and single-flight make duplicates effectively
      // free, so this stays for snappier UX rather than adding a dedupe layer.
      setTimeout(fetchStatus, 1_000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to start app';
      setActionError(message);
    }
  }, [projectId, fetchStatus, beginTransition]);

  const stopApp = useCallback(async () => {
    if (!projectId) return;
    setActionError(null);
    try {
      await appLifecycleApi.stopApp(projectId);
      beginTransition('stopping');
      setTimeout(fetchStatus, 1_000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to stop app';
      setActionError(message);
    }
  }, [projectId, fetchStatus, beginTransition]);

  return {
    state,
    startApp,
    stopApp,
    refreshStatus: fetchStatus,
    isTransitioning,
    actionError,
  };
}
